<template>
  <div
    class="min-h-screen flex flex-col"
    data-test="app-root"
    :data-app-ready="appReady ? 'true' : 'false'"
    :data-authenticated="isAuthenticated ? 'true' : 'false'"
    :data-access-loaded="accessLoaded ? 'true' : 'false'"
    :data-route-name="route.name || ''"
    :class="[demoEnabled && !isAuthenticated ? 'portal-demo' : '',
      isAuthPage
        ? ''
        : 'bg-gradient-to-br from-theme-bg-subtle via-theme-surface to-theme-palette-emerald/30 dark:from-theme-bg dark:via-theme-bg dark:to-theme-palette-emerald/20'
    ]"
  >
    <!-- Sidebar - only show on non-auth pages -->
    <sidebar v-if="!isAuthPage" class="flex-shrink-0" />

    <!-- Page Header - only show on non-auth pages and when authenticated -->
    <page-header v-if="!isAuthPage && (isAuthenticated || demoEnabled)" class="flex-shrink-0" />

    <!-- Tool-only prompts (newsletter, tokens) mean nothing in the API portal. -->
    <modal-newsletter v-if="!isApiOnly" />
    <payment-popup />
    <checkout-required-dialog />
    <modal-phone-verification />
    <upgrade-modal />
    <feedback-prompt />
    <feedback-launcher v-if="isAuthenticated && !isAuthPage" />
    <changelog-dialog />
    <changelog-sidebar v-if="isAuthenticated && !isAuthPage" />
    <benchmark-purchase-modal v-if="!isApiOnly" />
    <shadow-history-confirmation v-if="!isApiOnly" />
    <insufficient-tokens-modal v-if="!isApiOnly" />
    <token-top-up-dialog v-if="!isApiOnly" />
    <fullscreen-loader />
    <analytics-consent-banner />

    <transition name="nav-fade">
      <div v-if="updateExists" class="fixed bottom-4 left-4 right-4 z-toast">
        <div
          class="flex items-start gap-3 rounded-xl border border-theme-palette-emerald/40 bg-theme-palette-emerald/95 p-4 shadow-theme-elevated backdrop-blur
            dark:border-theme-palette-emerald dark:bg-theme-palette-emerald/40"
        >
          <div
            class="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-theme-palette-emerald/10 text-theme-palette-emerald dark:bg-theme-palette-emerald/60 dark:text-theme-palette-emerald"
          >
            <icon :path="mdiUpdate" size="18" />
          </div>
          <div class="flex-1">
            <p class="text-sm font-semibold text-theme-palette-emerald dark:text-theme-palette-emerald">
              {{ t('messages.update_available') }}
            </p>
            <p class="mt-0.5 text-xs text-theme-palette-emerald/80 dark:text-theme-palette-emerald/80">
              {{ t('messages.update_available_body') }}
            </p>
          </div>
          <AppButton
            @click="refreshApp"
            class="ml-2 inline-flex items-center rounded-lg bg-theme-palette-emerald px-3 py-1.5 text-xs font-medium text-theme-text-inverse shadow-sm
              transition hover:bg-theme-palette-emerald focus:outline-none focus:ring-2 focus:ring-theme-palette-emerald/30 focus:ring-offset-2
              dark:focus:ring-offset-theme-bg"
            tone="primary"
            size="xs"
            type="button"
          >
            {{ t('messages.update_available_action') }}
          </AppButton>
        </div>
      </div>
    </transition>

    <!-- Main content with conditional margin for sidebar and top padding for header -->
    <div :class="mainContentClass" class="flex-1 min-h-0 flex flex-col">
      <two-factor-setup-prompt v-if="!isAuthPage && isAuthenticated" />
      <dunning-banner v-if="!isAuthPage && isAuthenticated" />
      <demo-experience v-if="!isAuthPage && demoEnabled && !isAuthenticated" />
      <router-view v-slot="{ Component, route: viewRoute }">
        <route-transition :route-key="viewRoute.path">
          <!-- flex-1 min-h-0 is forwarded to the page root, preserving the
               full-height behaviour the bare <router-view> used to give it. -->
          <component :is="Component" class="flex-1 min-h-0" />
        </route-transition>
      </router-view>
      <footer-bar v-if="!route.meta.hideFooter && !isAuthPage" class="flex-shrink-0" />
    </div>

    <overlay v-show="isOverlayVisible" z-index="z-overlay" @overlay-click="overlayClick" />
  </div>
</template>

<script setup>
  import { demoEnabled } from '@/storage/demoMode';
  import DemoExperience from '@/components/demo/DemoExperience.vue';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
import { mdiUpdate } from '@mdi/js';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { authenticatedNewsRoute } from '@/router/guards/authGuard';
import { writePortalSessionHint } from '@/storage/portalSessionHint';

  import FingerprintJS from '@fingerprintjs/fingerprintjs';

  import UpgradeModal from '@/components/access/UpgradeModal.vue';
import DunningBanner from '@/components/billing/DunningBanner.vue';
import AnalyticsConsentBanner from '@/components/consent/AnalyticsConsentBanner.vue';
import FeedbackPrompt from '@/components/feedback/FeedbackPrompt.vue';
import FeedbackLauncher from '@/features/feedback/components/FeedbackLauncher.vue';
import Icon from '@/components/Icon';
import ModalNewsletter from '@/components/marketing/ModalNewsletter';
import PaymentPopup from '@/components/marketing/PaymentPopup.vue';
import CheckoutRequiredDialog from '@/components/billing/CheckoutRequiredDialog.vue';
import Sidebar from '@/components/navigation/Sidebar';
import BenchmarkPurchaseModal from '@/components/tokens/BenchmarkPurchaseModal.vue';
import ShadowHistoryConfirmation from '@/components/tokens/ShadowHistoryConfirmation.vue';
import InsufficientTokensModal from '@/components/tokens/InsufficientTokensModal.vue';
import TokenTopUpDialog from '@/components/tokens/TokenTopUpDialog.vue';
import FooterBar from '@/components/ui/nav/FooterBar';
import PageHeader from '@/components/ui/nav/PageHeader';
import Overlay from '@/components/ui/overlays/Overlay';
import FullscreenLoader from '@/components/ui/states/FullscreenLoader.vue';
import RouteTransition from '@/components/ui/transitions/RouteTransition.vue';
import { loadFavorites } from '@/composables/favorites';
import { loadProfile, loadRolePresets, needsOnboarding } from '@/composables/profile';
import {
  capturePostHogPageview,
  hasAnalyticsConsent,
  setAnalyticsConsent,
  trackAnalyticsEvent,
  useAnalyticsConsent,
} from '@/composables/useAnalytics';
import { useI18nInspectorMount } from '@/composables/useI18nInspector';
import { usePagePresence } from '@/composables/usePagePresence';
import { useSessionWakeValidation } from '@/composables/useSessionWakeValidation';
import ChangelogDialog from '@/features/changelog/components/ChangelogDialog.vue';
import ChangelogSidebar from '@/features/changelog/components/ChangelogSidebar.vue';
import { loadChangelog, resetChangelog } from '@/features/changelog/composables/useChangelog';
import { useNotificationServiceWorker } from '@/features/notifications/composables/useNotificationServiceWorker';
import ModalPhoneVerification from '@/pages/account/components/ModalPhoneVerification.vue';
import TwoFactorSetupPrompt from '@/pages/account/components/TwoFactorSetupPrompt.vue';
import { useAuthStore } from '@/stores/useAuthStore';
import { useDisplayStore } from '@/stores/useDisplayStore';
import { useEntitlementsStore } from '@/stores/useEntitlementsStore';
import { useTokenBalanceStore } from '@/stores/useTokenBalanceStore';
import { buildPageViewPayload, pageViewDedupeKey } from '@/utils/pageViewEvent';
import { afterCriticalContent } from '@/utils/deferNonCritical';
import { buildShellContentClass } from '@/utils/shellMotion';


  // Store and route
  const authStore = useAuthStore();
  const displayStore = useDisplayStore();
  const tokenBalanceStore = useTokenBalanceStore();
  const entitlementsStore = useEntitlementsStore();
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();
  usePagePresence(route);

  // Bridge push service-worker messages → in-app toast + feed refresh.
  useNotificationServiceWorker();

  // Mount global i18n inspector keyboard shortcut (CMD/Ctrl+Shift+T) for superusers
  useI18nInspectorMount();

  // State variables
  const isE2EMode = process.env.VUE_APP_E2E === 'true';
  useSessionWakeValidation(authStore, { disabled: isE2EMode });
  const appReady = ref(false);
  const updateExists = ref(false);
  const registration = ref(null);
  const isRefreshing = ref(false);

  const isSidebarOpen = computed(() => displayStore.isSidebarOpen);
  const isSidebarCollapsed = computed(() => displayStore.isSidebarCollapsed);
  const isOverlayVisible = computed(() => secondaryMenuItem.value);

  const isAuthenticated = computed(() => authStore.isAuthenticated);
  const isApiOnly = computed(() => entitlementsStore.isApiOnly);

  // Reflects whether the granular /api/v2/me/access set has resolved at least
  // once (the store's `accessLoadedAt` flips from 0 to a timestamp on the first
  // successful fetch). Surfaced as `data-access-loaded` on the app root so E2E
  // specs can wait on entitlement hydration instead of racing the network with
  // a `waitForResponse`.
  const accessLoaded = computed(() => entitlementsStore.accessLoadedAt > 0);

  const publishE2EReady = () => {
    if (!isE2EMode || typeof window === 'undefined') {
      return;
    }

    window.__PF_E2E_READY__ = {
      ...(window.__PF_E2E_READY__ || {}),
      app: appReady.value,
      authenticated: isAuthenticated.value,
      route: route.fullPath,
    };
  };

  watch([appReady, isAuthenticated, () => route.fullPath], publishE2EReady, { immediate: true });

  // Favourites and the changelog are not needed to paint the page: they run
  // after the main content has settled. Cancelled on logout so a late call
  // never goes out without a session.
  let cancelDeferred = [];
  const deferNonCritical = (fn) => cancelDeferred.push(afterCriticalContent(fn));

  watch(
    isAuthenticated,
    (authed) => {
      writePortalSessionHint(authed);
      cancelDeferred.forEach((cancel) => cancel());
      cancelDeferred = [];
      if (authed) {
        const newsRoute = authenticatedNewsRoute(route);
        if (newsRoute) router.replace(newsRoute).catch(() => {});
        deferNonCritical(loadFavorites);
        loadRolePresets().catch(() => {});
        tokenBalanceStore.refresh({ force: true }).catch(() => {});
        loadProfile()
          .then(() => {
            // Newly-onboarding users go straight to the dedicated funnel; the
            // route guard keeps them there until they complete or skip. Don't
            // redirect if they're already on the funnel or an auth page.
            if (
              needsOnboarding() &&
              !entitlementsStore.isApiOnly &&
              route.name !== 'onboarding' &&
              !isAuthPage.value
            ) {
              router.push('/onboarding').catch(() => {});
              return;
            }
            // Only after the onboarding decision: a first-run user should meet
            // the funnel, not a "what's new" dialog about a product they have
            // not seen yet. The backend decides whether the dialog opens.
            if (isAuthenticated.value) {
              deferNonCritical(() => loadChangelog({ surface: 'portal' }));
            }
          })
          .catch(() => {});
      } else {
        tokenBalanceStore.clear();
        resetChangelog();
      }
    },
    { immediate: true }
  );

  // Check if current route is an auth/form page
  const isPublicMarketingRoute = computed(() => {
    const routeName = route.name ? route.name.toString() : '';
    const isPublicName = ['about', 'contact', 'privacy-statement'].includes(routeName);
    const isApiDocs = routeName.startsWith('api-docs');
    const isSubscriptions = routeName.startsWith('subscriptions');
    // Public portal pages (src/router/routes/public.js) all carry a `public-` name
    // prefix and render their own PublicPageShell. These are standalone, shareable
    // pages: they must NEVER get the app sidebar/header — not even for a logged-in
    // visitor, who would otherwise see a link they shared render differently from
    // how their audience sees it. Hence this returns before the auth check below,
    // unlike the marketing routes, which only drop the chrome while anonymous.
    if (routeName.startsWith('public-')) {
      return true;
    }

    return !isAuthenticated.value && (isPublicName || isApiDocs || isSubscriptions);
  });

  const isAuthPage = computed(() => {
    return (
      route.meta?.formScreen === true ||
      route.meta?.embedded === true ||
      isPublicMarketingRoute.value
    );
  });

  // When we cross the auth boundary (login/onboarding/marketing ↔ shell),
  // the sidebar + header are added/removed instantly via `v-if`. Animating
  // the content margin in that moment makes the centred page glide in from
  // the lower-right corner and makes logout/session-expiry feel unstable.
  // So we suppress the margin transition for a single frame on every
  // `isAuthPage` flip; ordinary sidebar collapse toggles keep animating.
  const suppressShellMotion = ref(false);

  watch(isAuthPage, () => {
    suppressShellMotion.value = true;
    nextTick(() => {
      const restore = () => {
        suppressShellMotion.value = false;
      };
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(restore);
      } else {
        restore();
      }
    });
  });

  // Dynamic class for main content margin based on sidebar state.
  const mainContentClass = computed(() =>
    buildShellContentClass({
      isAuthPage: isAuthPage.value,
      isAuthenticated: isAuthenticated.value || demoEnabled.value,
      isSidebarCollapsed: isSidebarCollapsed.value,
      suppressMotion: suppressShellMotion.value,
    })
  );

  const secondaryMenuItem = computed({
    get: () => displayStore.secondaryMenu,
    set: (value) => displayStore.secondaryMenuToggle(value),
  });

  // Functions
  function toggleSidebar() {
    displayStore.setSidebarOpen(!isSidebarOpen.value);
  }

  const secondaryMenuClose = () => {
    secondaryMenuItem.value = null;
  };

  const menuClick = (event, item) => {
    if (item.darkModeToggle) {
      displayStore.darkMode();
    }
    if (item.menu) {
      displayStore.asideCompactToggle(false);
    }
    if (item.menuSecondary) {
      if (secondaryMenuItem.value && item.key === secondaryMenuItem.value.key) {
        secondaryMenuClose();
      } else {
        secondaryMenuItem.value = item;
      }
    }
  };

  const refreshApp = () => {
    updateExists.value = false;
    if (!registration.value || !registration.value.waiting) {
      window.location.reload();
      return;
    }

    registration.value.waiting.postMessage({ type: 'SKIP_WAITING' });
  };

  const updateAvailable = (event) => {
    registration.value = event.detail;
    updateExists.value = true;
  };
  const handleServiceWorkerControllerChange = () => {
    if (isRefreshing.value) return;
    isRefreshing.value = true;
    window.location.reload();
  };

  const menuClose = () => {
    displayStore.asideVisibilityToggle(false);
    secondaryMenuClose();
  };

  const overlayClick = () => {
    if (secondaryMenuItem.value) {
      secondaryMenuClose();
    }
  };

  // Lifecycle hooks
  onMounted(async () => {
    document.addEventListener('swUpdated', updateAvailable);
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        handleServiceWorkerControllerChange
      );
    }

    displayStore.defaultSidebarCollapsed();
    if (isE2EMode) {
      // Decide consent up front in E2E so the banner never overlays QA targets.
      setAnalyticsConsent(false);
      authStore.setVisitorId('e2e-visitor');
      appReady.value = true;
      return;
    }

    // Device fingerprinting only runs AFTER the user opts into analytics. Without
    // consent we still set a stable random device id so the authenticated
    // download client's request checksum keeps working — but we do NOT fingerprint.
    try {
      if (hasAnalyticsConsent()) {
        // ponytail: 2.5s. `FingerprintJS.load()` is a third-party network call
        // and nothing in the app needs its answer — it is analytics, not auth.
        // Blocked, throttled or just slow, it used to hold `appReady` (and the
        // visitor id every request checksum reads) for as long as it liked, and
        // forever if it threw. Give it about as long as a page load feels, then
        // fall back to the same stable random device id the no-consent path
        // already uses. Raise the bound only if fingerprint coverage matters
        // more than boot latency, which it does not.
        const visitorId = await Promise.race([
          FingerprintJS.load()
            .then((fp) => fp.get())
            .then((result) => result.visitorId),
          new Promise((resolve) => setTimeout(resolve, 2500)),
        ]);
        authStore.setVisitorId(visitorId || ensureDeviceId());
      } else {
        authStore.setVisitorId(ensureDeviceId());
      }
    } catch {
      authStore.setVisitorId(ensureDeviceId());
    } finally {
      // The visitor id is a nice-to-have; the app rendering is not. Nothing
      // above may gate this.
      appReady.value = true;
    }
  });

  function ensureDeviceId() {
    try {
      const existing = localStorage.getItem('visitorId');
      if (existing && existing !== 'null') {
        return existing;
      }
    } catch {
      // ignore storage failures
    }
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `device-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  onBeforeUnmount(() => {
    document.removeEventListener('swUpdated', updateAvailable);
    if (navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        handleServiceWorkerControllerChange
      );
    }
  });

  // Token-safe pageview tracking. We send the matched route *pattern*
  // (e.g. /s/:token), never the resolved path — so secret path params (share
  // tokens, recover/verify codes) can never leak into analytics. We dedupe on
  // logical-page identity (pattern + path params) so query-only navigations
  // (filters/tabs syncing to the URL) don't inflate pageview counts.
  // The token-safety + dedupe logic lives in a pure, unit-tested util.
  let lastPageViewKey = null;

  function trackPageView(to, from) {
    if (!to) {
      return;
    }
    const key = pageViewDedupeKey(to);
    if (key === lastPageViewKey) {
      return;
    }
    lastPageViewKey = key;
    trackAnalyticsEvent('page.viewed', buildPageViewPayload(to, from));
    capturePostHogPageview(to);
  }

  watch(
    route,
    (to, from) => {
      trackPageView(to, from);

      // Only fetch user info if we don't have it yet
      if (!authStore.isAuthenticated || !authStore.email) {
        authStore.getUserInformation();
      }
    },
    { deep: false }
  );

  // Initial pageview: the route watcher above does NOT fire for the landing
  // route, so emit it once the router has resolved. No-ops without consent; the
  // dedupe guard prevents a double with the first navigation.
  if (router && typeof router.isReady === 'function') {
    router.isReady().then(() => trackPageView(route, undefined));
  }

  // When consent is granted mid-session (banner accept), capture the current
  // page as the entry pageview that was suppressed before opt-in.
  const { consent: analyticsConsent } = useAnalyticsConsent();
  watch(analyticsConsent, (value) => {
    if (value === 'granted') {
      lastPageViewKey = null;
      trackPageView(route, undefined);
    }
  });
</script>
